// Les événements de puzzles (la chasse d'Halloween...). Pendant un événement, chaque coffre
// de quête ouvert donne une pièce de puzzle ; un puzzle complet débloque une pièce d'avatar.
// Les règles vivent côté back (AvatarEventService). Ici : la vérification du bloc collé dans
// l'admin, et ce que l'affichage calcule à partir de l'état rendu par /api/avatar/event.
//
// Un bloc d'événement :
// {
//   "slug": "halloween-2026",
//   "name": { "fr": "La chasse d'Halloween", "en": "The Halloween Hunt" },
//   "from": "2026-10-19", "to": "2026-11-01",      du premier au dernier jour inclus, heure de Paris
//   "lastChanceTo": "2026-11-04",                   (facultatif) jusqu'à quand finir un puzzle en fragments
//   "teaserDays": 7,                                (facultatif) l'événement s'annonce ce nombre de jours avant son début ;
//                                                   d'ici là, le site ne montre rien. 7 par défaut, 0 pour ne rien annoncer
//   "puzzles": [{ "item": "headwear:witch", "size": 9, "image": "/images/events/halloween-2026/witch.webp" }],
//                                                   une pièce d'avatar par puzzle ; `image` (facultatif) est
//                                                   l'illustration carrée découpée en pièces. Sans elle, le
//                                                   puzzle montre l'avatar du membre avec la pièce à gagner.
//   "piecePrice": 150,                              prix d'une pièce en fragments (0 : pas d'achat)
//   "buyPerDay": 1, "buyPerDayLastChance": 2,       pièces achetables par jour
//   "milestones": [{ "target": 150, "fragments": 50 }, { "target": 350, "item": "mouth:fangs" }],
//                                                   paliers de l'objectif commun, en pièces posées par tous
//   "badgeId": 27,                                  (facultatif) badge de ceux qui finissent tous les puzzles
//   "topFragments": [150, 100, 50],                 (facultatif) fragments des meilleurs contributeurs
//   "pausesLeagueGoal": true,                       l'objectif commun à l'XP de la ligue s'efface pendant l'événement
//   "theme": {                                      (facultatif) l'habillage des quêtes du jour pendant l'événement
//     "questsTitle": { "fr": "Quêtes d'Halloween", "en": "Halloween quests" },
//     "chests": { "common": "/images/events/halloween-2026/chest-common.webp", "uncommon": "…", "rare": "…", "epic": "…", "legendary": "…" },
//                                                   les coffres de l'événement, un par rareté ; une rareté absente garde son coffre habituel
//     "color": "#F08A24", "dark": "#2A1F4D"         la couleur d'accent et la teinte du bandeau
//     "badge": "/images/events/halloween-2026/badge.webp"   l'image du badge, montrée quand le membre finit tous les puzzles
//     "sounds": { "pick": "/static/sounds/…mp3", "land": "…", "done": "…",
//                 "appear": "…", "taps": ["…", "…", "…", "…", "…"], "upgrade": "…", "open": "…", "reveal": "…", "recap": "…" }
//                                                   (facultatif) les sons de l'événement. Le puzzle : la pièce sortie du coffre,
//                                                   celle qui s'enclenche, le puzzle fini. Le coffre : son arrivée, les tapes
//                                                   (une par tape, la dernière se répète), la rareté qui monte, l'ouverture,
//                                                   chaque récompense révélée (sauf la pièce, qui garde `pick`), le récapitulatif.
//                                                   Un son absent garde celui par défaut. Un son du coffre est calé sur le volume
//                                                   de celui qu'il remplace (public/static/sounds/).
//   }
// }

export const CHEST_RARITIES = ["common", "uncommon", "rare", "epic", "legendary"];
// Les sons qu'un événement peut remplacer. Le puzzle : la pièce sortie du coffre, la pièce qui s'enclenche,
// le puzzle fini. Le coffre : son arrivée, la rareté qui monte, l'ouverture, chaque récompense révélée, le
// récapitulatif. Les tapes sur le coffre ont leur propre clé, `taps` : une liste, un son par tape.
export const EVENT_SOUNDS = ["pick", "land", "done", "appear", "upgrade", "open", "reveal", "recap"];
const MAX_TAP_SOUNDS = 5;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MOMENT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const bound = (value, end) => (DAY.test(value) ? `${value} ${end ? "23:59:59" : "00:00:00"}` : value);
const whole = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;

// Vérifie un bloc. `pieces` : l'index des pièces en base (un puzzle ou un palier doit débloquer
// une pièce qui existe). Rend { event } (le bloc nettoyé) ou { errors }.
export function validateEvent(block, pieces = null) {
    const errors = [];
    if (!block || typeof block !== "object" || Array.isArray(block)) return { errors: ["le bloc doit être un objet { slug, name, from, to, puzzles… }"] };
    const known = (item, where) => {
        if (pieces && !pieces[item]) errors.push(`${where} : la pièce « ${item} » n'existe pas (collez d'abord son bloc)`);
    };

    if (typeof block.slug !== "string" || !/^[a-z0-9-]{1,60}$/.test(block.slug)) errors.push("slug s'écrit en minuscules, chiffres et tirets (halloween-2026)");
    const name = { fr: String(block.name?.fr || "").trim().slice(0, 80), en: String(block.name?.en || "").trim().slice(0, 80) };
    if (!name.fr || !name.en) errors.push("name doit donner le nom en français et en anglais : { fr, en }");

    const event = { slug: block.slug, name, from: bound(block.from, false), to: bound(block.to, true) };
    if (!MOMENT.test(event.from) || !MOMENT.test(event.to)) errors.push("from et to s'écrivent « 2026-10-19 »");
    else if (event.from >= event.to) errors.push("l'événement finit avant de commencer");
    if (block.lastChanceTo !== undefined && block.lastChanceTo !== null && block.lastChanceTo !== "") {
        event.lastChanceTo = bound(block.lastChanceTo, true);
        if (!MOMENT.test(event.lastChanceTo)) errors.push("lastChanceTo s'écrit « 2026-11-04 »");
        else if (event.lastChanceTo <= event.to) errors.push("lastChanceTo doit tomber après la fin de l'événement");
    }

    event.teaserDays = block.teaserDays ?? 7;
    if (!whole(event.teaserDays, 0, 60)) errors.push("teaserDays est le nombre de jours d'annonce avant le début, de 0 à 60");

    const puzzles = Array.isArray(block.puzzles) ? block.puzzles : [];
    if (!puzzles.length || puzzles.length > 12) errors.push("puzzles doit lister de 1 à 12 puzzles : [{ item, size }]");
    event.puzzles = puzzles.map((puzzle) => {
        if (typeof puzzle?.item !== "string") errors.push("chaque puzzle donne la pièce d'avatar qu'il débloque : item");
        else known(puzzle.item, "puzzles");
        if (!whole(puzzle?.size, 1, 25)) errors.push(`${puzzle?.item || "puzzle"} : size est le nombre de pièces, de 1 à 25`);
        const clean = { item: puzzle?.item, size: puzzle?.size };
        if (puzzle?.image !== undefined && puzzle.image !== null && puzzle.image !== "") {
            if (typeof puzzle.image === "string" && /^(\/[\w\-./]+|https:\/\/[\w\-./%~?=&]+)$/.test(puzzle.image)) clean.image = puzzle.image;
            else errors.push(`${puzzle?.item || "puzzle"} : image est une adresse du site (/images/…) ou une adresse https`);
        }
        return clean;
    });
    if (new Set(event.puzzles.map((puzzle) => puzzle.item)).size !== event.puzzles.length) errors.push("deux puzzles débloquent la même pièce");

    event.piecePrice = block.piecePrice ?? 0;
    event.buyPerDay = block.buyPerDay ?? 0;
    event.buyPerDayLastChance = block.buyPerDayLastChance ?? 0;
    if (!whole(event.piecePrice, 0, 100000)) errors.push("piecePrice est un prix en fragments (0 pour interdire l'achat)");
    if (!whole(event.buyPerDay, 0, 50) || !whole(event.buyPerDayLastChance, 0, 50)) errors.push("buyPerDay et buyPerDayLastChance sont des nombres de pièces par jour");

    const milestones = Array.isArray(block.milestones) ? block.milestones : [];
    event.milestones = milestones.map((milestone) => {
        const clean = { target: milestone?.target };
        if (!whole(milestone?.target, 1, 1000000)) errors.push("chaque palier donne sa cible en pièces : target");
        if (milestone?.fragments !== undefined && milestone.fragments !== null) {
            if (whole(milestone.fragments, 1, 100000)) clean.fragments = milestone.fragments;
            else errors.push("fragments d'un palier : un nombre entier");
        }
        if (milestone?.item) {
            clean.item = milestone.item;
            known(milestone.item, "milestones");
        }
        if (!clean.fragments && !clean.item) errors.push(`le palier ${milestone?.target ?? ""} ne donne rien : fragments ou item`);
        return clean;
    });
    if (event.milestones.some((milestone, index) => index > 0 && milestone.target <= event.milestones[index - 1].target)) errors.push("les paliers se rangent du plus petit au plus grand");

    if (block.badgeId !== undefined && block.badgeId !== null && block.badgeId !== "") {
        if (whole(block.badgeId, 1, 1000000)) event.badgeId = block.badgeId;
        else errors.push("badgeId est le numéro d'un badge");
    }
    event.topFragments = Array.isArray(block.topFragments) ? block.topFragments : [];
    if (event.topFragments.length > 10 || event.topFragments.some((amount) => !whole(amount, 1, 100000))) errors.push("topFragments : au plus 10 montants entiers, du premier au dernier");
    event.pausesLeagueGoal = block.pausesLeagueGoal !== false;

    if (block.theme !== undefined && block.theme !== null) {
        const theme = {};
        const hex = /^#[0-9a-fA-F]{6}$/;
        const address = /^(\/[\w\-./]+|https:\/\/[\w\-./%~?=&]+)$/;
        if (block.theme.questsTitle?.fr && block.theme.questsTitle?.en) theme.questsTitle = { fr: String(block.theme.questsTitle.fr).slice(0, 60), en: String(block.theme.questsTitle.en).slice(0, 60) };
        if (block.theme.chests !== undefined && block.theme.chests !== null) {
            theme.chests = {};
            for (const rarity of CHEST_RARITIES) {
                const chest = block.theme.chests[rarity];
                if (chest === undefined) continue;
                if (typeof chest === "string" && address.test(chest)) theme.chests[rarity] = chest;
                else errors.push(`theme.chests.${rarity} est une adresse du site (/images/…) ou une adresse https`);
            }
        }
        if (block.theme.badge !== undefined && block.theme.badge !== null && block.theme.badge !== "") {
            if (typeof block.theme.badge === "string" && address.test(block.theme.badge)) theme.badge = block.theme.badge;
            else errors.push("theme.badge est une adresse du site (/images/…) ou une adresse https");
        }
        if (block.theme.sounds !== undefined && block.theme.sounds !== null) {
            theme.sounds = {};
            const soundFile = (sound) => typeof sound === "string" && address.test(sound) && /\.(mp3|ogg|wav)$/.test(sound);
            for (const key of EVENT_SOUNDS) {
                const sound = block.theme.sounds[key];
                if (sound === undefined || sound === null || sound === "") continue;
                if (soundFile(sound)) theme.sounds[key] = sound;
                else errors.push(`theme.sounds.${key} est l'adresse d'un son (.mp3, .ogg ou .wav)`);
            }
            const taps = block.theme.sounds.taps;
            if (taps !== undefined && taps !== null) {
                if (Array.isArray(taps) && taps.length >= 1 && taps.length <= MAX_TAP_SOUNDS && taps.every(soundFile)) theme.sounds.taps = taps;
                else errors.push(`theme.sounds.taps est une liste de 1 à ${MAX_TAP_SOUNDS} sons (.mp3, .ogg ou .wav), un par tape`);
            }
        }
        for (const key of ["color", "dark"]) {
            if (block.theme[key] === undefined) continue;
            if (hex.test(block.theme[key])) theme[key] = block.theme[key];
            else errors.push(`theme.${key} est une couleur #RRGGBB`);
        }
        event.theme = theme;
    }

    return errors.length ? { errors } : { event };
}

export const eventName = (event, locale) => event?.name?.[locale === "en" ? "en" : "fr"] || "";
export const totalPieces = (event) => (event?.puzzles || []).reduce((sum, puzzle) => sum + puzzle.size, 0);

// Le palier que la communauté vise : le premier qui n'est pas atteint, ou null s'ils le sont tous.
export const nextMilestone = (event, total) => (event?.milestones || []).find((milestone) => total < milestone.target) || null;
